import React, { type ComponentProps, useCallback } from 'react';
import { type Href, useLocalSearchParams, useRouter } from 'expo-router';
import { StyleSheet } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';

import { BodyText, Button, DisplayText, theme } from '@sg/garnish';

import { useClickToClaimMachine } from '@order/features-loyalty';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { LoadingAnimation, NotFoundView, SignedOutViewContent } from '../../components';
import { useIsLoggedIn, useIsLoggingIn } from '../../context/AuthMachine';
import { useNavigateBack } from '../../navigation';
import {
  APPLY_REWARD_FADE_IN_DURATION,
  CLAIMED_REWARD_DESTINATION,
} from './ApplyRewardScreen.constants';
import { ApplyRewardModal } from './components';
import { useClaimStatusMessages } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const ApplyRewardScreen = () => {
  const isClickToClaimEnabled = useFeatureFlag('CELS-5669-order-click-to-claim-fe');

  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();

  const { offerId } = useLocalSearchParams<RouteParams>();

  // ─── Gates ───────────────────────────────────────────────────────────

  if (!isClickToClaimEnabled || !offerId) {
    return (
      <ApplyRewardModal>
        <NotFoundView withoutFooter />
      </ApplyRewardModal>
    );
  }

  if (isLoggingIn) {
    return (
      <ApplyRewardModal>
        <LoadingAnimation />
      </ApplyRewardModal>
    );
  }

  if (!isLoggedIn) {
    return (
      <ApplyRewardModal palette="cream">
        <SignedOutViewContent redirectTo={buildRedirectTarget(offerId)} />
      </ApplyRewardModal>
    );
  }

  return <ApplyRewardScreenReady offerId={offerId} />;
};

// ─────────────────────────────────────────────────────────────────────────────

const ApplyRewardScreenReady = (props: ApplyRewardScreenReadyProps) => {
  const { offerId } = props;

  const router = useRouter();
  const navigateBack = useNavigateBack();

  const { status, reward } = useClickToClaimMachine({ offerId });

  const { header, body, cta } = useClaimStatusMessages({ status, rewardName: reward?.name });

  const isClaiming = status === 'claiming';
  const isClaimed = status === 'claimed';

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleContinue = useCallback(() => {
    if (isClaimed) {
      router.dismissTo(CLAIMED_REWARD_DESTINATION);

      return;
    }

    navigateBack();
  }, [isClaimed, navigateBack, router]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ApplyRewardModal>
      <Animated.View
        key={status}
        entering={FadeIn.duration(APPLY_REWARD_FADE_IN_DURATION)}
        style={styles.textContainer}
      >
        <DisplayText sizeMatch={HEADER_SIZE_MATCH} style={styles.headerText}>
          {header}
        </DisplayText>

        <BodyText sizeMatch={BODY_SIZE_MATCH} style={styles.bodyText}>
          {body}
        </BodyText>
      </Animated.View>

      <Button
        size="large-wide"
        style={styles.button}
        palette="kaleLightest"
        accessibilityLabel={cta}
        isLoading={isClaiming}
        onPress={handleContinue}
      >
        <BodyText sizeMatch={BODY_SIZE_MATCH} style={styles.buttonText}>
          {cta}
        </BodyText>
      </Button>
    </ApplyRewardModal>
  );
};

// ─── Pure Functions ──────────────────────────────────────────────────────────

const buildRedirectTarget = (offerId: string): Href => {
  return `/apply-reward/${encodeURIComponent(offerId)}`;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const HEADER_SIZE_MATCH: ComponentProps<typeof DisplayText>['sizeMatch'] = ['32', '40'];
const BODY_SIZE_MATCH: ComponentProps<typeof BodyText>['sizeMatch'] = ['16', '18'];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  textContainer: {
    gap: theme.spacing['6'],
  },
  headerText: {
    color: theme.colors.SWEET_CORN,
    textAlign: 'center',
  },
  bodyText: {
    color: theme.colors.WHITE,
    textAlign: 'center',
  },
  button: {
    backgroundColor: theme.colors.SWEET_CORN,
  },
  buttonText: {
    color: theme.colors.DARK_KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type RouteParams = {
  offerId?: string;
};

type ApplyRewardScreenReadyProps = {
  offerId: string;
};
